import { useState, useEffect, useCallback } from 'react';
import { renderDiagram, parseDiagram, exportSVG, exportPNG } from '../services/mermaidService';
import { useSettings } from '../contexts/SettingsContext';

/**
 * Custom hook for Mermaid rendering
 * @param {string} code - Mermaid diagram code
 * @returns {Object} Mermaid rendering utilities
 */
const useMermaid = (code) => {
  const [svg, setSvg] = useState('');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const { settings } = useSettings();

  // Render the diagram
  const render = useCallback(async () => {
    if (!code) {
      setSvg('');
      setError(null);
      return;
    }

    setLoading(true);
    try {
      // Parse the diagram to check for syntax errors
      await parseDiagram(code);
      
      // Render the diagram
      const renderedSvg = await renderDiagram(code, null, {
        theme: settings.theme === 'dark' ? 'dark' : 'default',
      });
      
      setSvg(renderedSvg);
      setError(null);
    } catch (err) {
      console.error('Error rendering diagram:', err);
      setError(err.message || 'Error rendering diagram');
      setSvg('');
    } finally {
      setLoading(false);
    }
  }, [code, settings.theme]);

  // Render the diagram when the code or theme changes
  useEffect(() => {
    if (settings.renderOnChange) {
      const debounceTimer = setTimeout(() => {
        render();
      }, 300); // Debounce to avoid rendering on every keystroke
      
      return () => clearTimeout(debounceTimer);
    }
  }, [code, settings.theme, settings.renderOnChange, render]);

  // Export the diagram as SVG
  const handleExportSVG = useCallback(async () => {
    try {
      const svgString = await exportSVG(code, {
        theme: settings.theme === 'dark' ? 'dark' : 'default',
      });
      
      // Create a blob from the SVG string
      const blob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
      
      // Create a download link
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'diagram.svg';
      
      // Trigger the download
      document.body.appendChild(link);
      link.click();
      
      // Clean up
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      return true;
    } catch (err) {
      console.error('Error exporting SVG:', err);
      setError(err.message || 'Error exporting SVG');
      return false;
    }
  }, [code, settings.theme]);

  // Export the diagram as PNG
  const handleExportPNG = useCallback(async () => {
    try {
      const pngBlob = await exportPNG(code, {
        theme: settings.theme === 'dark' ? 'dark' : 'default',
      });
      
      // Create a download link
      const url = URL.createObjectURL(pngBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'diagram.png';
      
      // Trigger the download
      document.body.appendChild(link);
      link.click();
      
      // Clean up
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      return true;
    } catch (err) {
      console.error('Error exporting PNG:', err);
      setError(err.message || 'Error exporting PNG');
      return false;
    }
  }, [code, settings.theme]);

  return {
    svg,
    error,
    loading,
    render,
    exportSVG: handleExportSVG,
    exportPNG: handleExportPNG,
  };
};

export default useMermaid; 